import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout, { inputCls, btnPrimary } from "@/pages/AuthLayout";

export default function Login() {
  const [mode, setMode] = useState("magic"); // magic | password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { applyAuth } = useAuth();
  const nav = useNavigate();

  const doMagic = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/magic-link/request", { email });
      setSent(true);
      toast.success(data.message || "Check your email for a sign-in link.");
      if (data.dev_link) console.log("DEV magic link:", data.dev_link);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const doPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      applyAuth(data);
      toast.success("Welcome back.");
      nav("/app");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthLayout title="Sign in" subtitle="Access your toolkit. Magic link is the quickest way in.">
      {mode === "magic" ? (
        sent ? (
          <div data-testid="magic-sent" className="text-center py-4">
            <div className="mx-auto h-12 w-12 rounded-full bg-nm-green/15 text-nm-green flex items-center justify-center"><Mail size={22} /></div>
            <p className="text-nm-ink mt-4 font-medium">Link sent to {email}</p>
            <p className="text-sm text-nm-neutral mt-2">Open the email and click the button to sign in. It expires in 30 minutes.</p>
            <button onClick={() => setSent(false)} className="text-sm text-nm-primary mt-5 hover:underline" data-testid="resend-link">Use a different email</button>
          </div>
        ) : (
          <form onSubmit={doMagic} className="space-y-4">
            <div>
              <label className="text-sm text-nm-ink font-medium">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="you@company.com" data-testid="magic-email" />
            </div>
            <button type="submit" disabled={loading} className={btnPrimary} data-testid="magic-submit">
              {loading ? "Sending…" : <>Send magic link <ArrowRight size={16} /></>}
            </button>
          </form>
        )
      ) : (
        <form onSubmit={doPassword} className="space-y-4">
          <div>
            <label className="text-sm text-nm-ink font-medium">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="you@company.com" data-testid="login-email" />
          </div>
          <div>
            <label className="text-sm text-nm-ink font-medium">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={`${inputCls} mt-1.5`} placeholder="••••••••" data-testid="login-password" />
          </div>
          <button type="submit" disabled={loading} className={btnPrimary} data-testid="login-submit">
            {loading ? "Signing in…" : <>Sign in <ArrowRight size={16} /></>}
          </button>
        </form>
      )}

      <div className="mt-6 text-center">
        <button onClick={() => { setMode(mode === "magic" ? "password" : "magic"); setSent(false); }} className="text-sm text-nm-primary hover:underline" data-testid="toggle-auth-mode">
          {mode === "magic" ? "Use email & password instead" : "Use a magic link instead"}
        </button>
      </div>
      <p className="mt-4 text-center text-sm text-nm-neutral">
        New here? <Link to="/register" className="text-nm-coral font-medium hover:underline" data-testid="to-register">Create an account</Link>
      </p>
    </AuthLayout>
  );
}
